import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../../components/Modal';
import SearchSelect, {
    SearchSelectOption,
} from '../../components/SearchSelect';
import { useDictText } from '../../components/DictSelect';
import { parseFields } from './SchemaEditModal';
import { ApiError, downloadFile } from '../../api/request';
import { deviceLabel, Device, listDevices } from '../../api/device';
import {
    archUsable,
    importParams,
    ImportParamsResult,
    listProcessArchs,
    listSchemas,
    paramImportTemplatePath,
    ParamSchema,
    ProcessArch,
    resolveSchema,
} from '../../api/parameter';
import { useSchemaKeyLabel } from '../../utils/useSchemaKeyLabel';
import { useParamKeyOptions } from './useParamKeyOptions';
import { modeSelectOptions } from './ModeOptGroups';

interface ParamImportModalProps {
    open: boolean;
    onClose: () => void;
    /** 导入成功 (valid > 0) 后回调, 刷新参数列表 */
    onImported?: () => void;
}

// 参数批量导入 (xlsx): 选 框架 → 设备 → 加工模式 → 加工类型 (与参数录入同一套候选) → 自动命中参数模板
// → 下载导入模板 → 上传 → 展示逐行校验结果.
// 模板列由后端按命中模板动态生成; 选定的 设备/加工模式/加工类型 是整份文件的固定值, xlsx 不出这几列,
// 填的人只管材料与参数值. 模板 id 与预设值随文件走 (隐藏 __tpl sheet), 上传时后端补到每行并逐行自检.
// 动态字段列必须命中字段字典, 未知列整文件报错. 有效行落库为 draft, 走评审发布.
const ParamImportModal = ({
    open,
    onClose,
    onImported,
}: ParamImportModalProps) => {
    const [archs, setArchs] = useState<ProcessArch[]>([]);
    const [archCode, setArchCode] = useState('laser-frame');
    const [schemas, setSchemas] = useState<ParamSchema[]>([]);
    const [devices, setDevices] = useState<Device[]>([]);
    // 预设的关键字段 (都可空: 空 = 该列照常出列, 由填表的人逐行填)
    const [deviceKey, setDeviceKey] = useState('');
    const [processingMode, setProcessingMode] = useState('');
    const [subProcess, setSubProcess] = useState('');
    // 按上面三项命中的模板 (null = 未命中任何模板)
    const [tpl, setTpl] = useState<ParamSchema | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState<ImportParamsResult | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const [tplBusy, setTplBusy] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const lightText = useDictText('light_source');

    useEffect(() => {
        if (!open) {
            setFile(null);
            setBusy(false);
            setError('');
            setResult(null);
            setDragOver(false);
            return;
        }
        listProcessArchs()
            .then((list) => {
                const usable = list.filter(archUsable);
                setArchs(usable);
                if (
                    usable.length > 0 &&
                    !usable.find((a) => a.code === archCode)
                ) {
                    setArchCode(usable[0].code);
                }
            })
            .catch(() => setArchs([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // 切框架: 模板清单与设备清单跟着换, 已选的关键字段不再属于本框架, 一律清空
    useEffect(() => {
        if (!open || !archCode) return;
        setDeviceKey('');
        setProcessingMode('');
        setSubProcess('');
        listSchemas(archCode)
            .then(setSchemas)
            .catch(() => setSchemas([]));
        listDevices({ archCode, pageSize: 100 })
            .then((res) => setDevices(res.list || []))
            .catch(() => setDevices([]));
    }, [open, archCode]);

    const family = devices.find((d) => d.deviceKey === deviceKey)?.family || '';
    const {
        modeOptions,
        modeGroups,
        modeLabel,
        groupCodeOf,
        subProcessShownOpts,
    } = useParamKeyOptions({
        archCode,
        family,
        deviceKey,
        processingMode,
        archSchemas: schemas,
    });
    const deviceOptions: SearchSelectOption[] = useMemo(
        () =>
            devices.map((d) => ({
                value: d.deviceKey,
                label: deviceLabel(d, lightText),
            })),
        [devices, lightText],
    );
    const modeOpts = useMemo(
        () => modeSelectOptions(modeGroups, modeLabel),
        [modeGroups, modeLabel],
    );

    // 上游一变, 已选的下游值不在新候选里就清掉 (避免预设一份命不中模板的组合)
    useEffect(() => {
        if (
            processingMode &&
            !modeOptions.some((m) => m.value === processingMode)
        ) {
            setProcessingMode('');
        }
    }, [modeOptions, processingMode]);
    useEffect(() => {
        if (
            subProcess &&
            !subProcessShownOpts.some((o) => o.code === subProcess)
        ) {
            setSubProcess('');
        }
    }, [subProcessShownOpts, subProcess]);

    // 三项一变就按模板键最特异解析 (与录入页、后端下载同一逻辑), 让人下载前就看到会出哪张模板的列
    useEffect(() => {
        if (!open || !archCode) return;
        let alive = true;
        resolveSchema(
            archCode,
            {
                family,
                groupCode: groupCodeOf(processingMode),
                processType: subProcess,
            },
            deviceKey,
        )
            .then((t) => alive && setTpl(t))
            .catch(() => alive && setTpl(null));
        return () => {
            alive = false;
        };
    }, [
        open,
        archCode,
        family,
        deviceKey,
        processingMode,
        subProcess,
        groupCodeOf,
    ]);

    const { keyLabel } = useSchemaKeyLabel(open ? archCode : '');

    const pickFile = (f: File | null | undefined) => {
        if (!f) return;
        if (!/\.xlsx$/i.test(f.name)) {
            setError('仅支持 .xlsx 文件');
            return;
        }
        setFile(f);
        setResult(null);
        setError('');
    };

    // 模板下载必须走 downloadFile (fetch 带 utoken), 不能用 <a href download>:
    // 浏览器直接跳转带不上鉴权头, 线上必然 20101 "token验证失效".
    const onDownloadTemplate = async () => {
        setTplBusy(true);
        setError('');
        try {
            await downloadFile(
                paramImportTemplatePath(archCode, {
                    deviceKey,
                    processingMode,
                    subProcess,
                }),
                `param-import-${archCode}${tpl?.id ? `-tpl${tpl.id}` : ''}.xlsx`,
            );
        } catch (e) {
            setError(
                `模板下载失败: ${e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)}`,
            );
        } finally {
            setTplBusy(false);
        }
    };

    const onSubmit = async () => {
        if (!file) {
            setError('请先选择 xlsx 文件');
            return;
        }
        setBusy(true);
        setError('');
        setResult(null);
        try {
            const res = await importParams(archCode, file);
            setResult(res);
            if (res.valid > 0 && onImported) onImported();
        } catch (e) {
            setError(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
        } finally {
            setBusy(false);
        }
    };

    const invalid = result?.invalid || [];

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="批量导入参数 (xlsx)"
            width={720}
        >
            <div className="space-y-4">
                <div>
                    <label className="field-label">目标工艺架构</label>
                    <SearchSelect
                        className="w-full"
                        value={archCode}
                        onChange={(v) => {
                            setArchCode(v);
                            setResult(null);
                        }}
                        options={
                            archs.length === 0
                                ? [
                                      {
                                          value: 'laser-frame',
                                          label: '🔲 激光框机',
                                      },
                                  ]
                                : archs.map((a) => ({
                                      value: a.code,
                                      label: `${a.icon} ${a.label}`,
                                  }))
                        }
                    />
                </div>

                <div className="grid grid-cols-3 gap-3">
                    <div>
                        <label className="field-label">设备</label>
                        <SearchSelect
                            className="w-full"
                            options={deviceOptions}
                            value={deviceKey}
                            onChange={(v) => setDeviceKey(v)}
                            placeholder="搜索设备"
                            clearable
                        />
                    </div>
                    <div>
                        <label className="field-label">
                            加工模式
                            <span className="text-slate-400 text-xs ml-1">
                                (有模板的模式)
                            </span>
                        </label>
                        <SearchSelect
                            className="w-full"
                            value={processingMode}
                            onChange={(v) => setProcessingMode(v)}
                            placeholder="请选择…"
                            options={modeOpts}
                            clearable
                        />
                    </div>
                    <div>
                        <label className="field-label">
                            加工类型
                            <span className="text-slate-400 text-xs ml-1">
                                (有模板的类型)
                            </span>
                        </label>
                        <SearchSelect
                            className="w-full"
                            value={subProcess}
                            onChange={(v) => setSubProcess(v)}
                            placeholder="请选择"
                            options={subProcessShownOpts.map((o) => ({
                                value: o.code,
                                label: o.label,
                            }))}
                            clearable
                        />
                    </div>
                </div>
                <div className="text-xs">
                    <span className="text-slate-500">命中参数模板: </span>
                    {tpl ? (
                        <span className="text-slate-700">
                            #{tpl.id} {keyLabel(tpl)} ·{' '}
                            {parseFields(tpl.fields).length} 个字段
                        </span>
                    ) : (
                        <span className="text-rose-600">
                            当前组合没有命中任何参数模板, 去「参数模板」页补模板
                        </span>
                    )}
                    <div className="text-slate-400 mt-1">
                        选了的 设备 / 加工模式 / 加工类型 是整份文件的固定值,
                        xlsx 里不再出这几列, 上传时自动补到每一行,
                        只需填材料与参数值; 没选的项照常出列、逐行填. 枚举列
                        (加工头 / 检查结果等) 在 xlsx 里是中文下拉.
                    </div>
                </div>

                <div className="text-xs text-slate-500">
                    先
                    <a
                        className="text-brand-600 hover:underline mx-1 cursor-pointer"
                        onClick={onDownloadTemplate}
                    >
                        {tplBusy ? '下载中…' : '下载导入模板'}
                    </a>
                    按表头填写后上传. 表头格式「显示名(字段key)」;
                    固定列之外的列都是动态 Schema 字段, 必须在 Schema
                    字段字典中, 未知列会整个文件报错. 有效行落库为草稿,
                    走「提交评审 → 审批」后发布.
                </div>

                <div
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        pickFile(e.dataTransfer.files?.[0]);
                    }}
                    className={`border-2 border-dashed rounded-lg p-6 text-center text-sm cursor-pointer ${
                        dragOver
                            ? 'border-brand-400 bg-brand-50/40'
                            : 'border-slate-200 hover:border-slate-300'
                    }`}
                >
                    {file ? (
                        <div className="text-slate-700 font-mono text-xs">
                            {file.name}{' '}
                            <span className="text-slate-400">
                                ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                        </div>
                    ) : (
                        <span className="text-slate-400">
                            点击选择 或 拖拽 xlsx 文件到此处
                        </span>
                    )}
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        className="hidden"
                        onChange={(e) => {
                            pickFile(e.target.files?.[0]);
                            e.target.value = '';
                        }}
                    />
                </div>

                {error && (
                    <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                        {error}
                    </div>
                )}

                {result && (
                    <div className="card overflow-x-auto">
                        <div className="px-3 py-2 bg-slate-50 text-sm flex items-center gap-3">
                            <span>共 {result.total} 行</span>
                            <span className="text-emerald-600">
                                导入草稿 {result.valid}
                            </span>
                            {invalid.length > 0 && (
                                <span className="text-rose-600">
                                    无效 {invalid.length}
                                </span>
                            )}
                        </div>
                        {invalid.length > 0 && (
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 border-t border-slate-100">
                                    <tr>
                                        <th className="th w-16">行号</th>
                                        <th className="th">无效原因</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invalid.map((r) => (
                                        <tr key={r.row}>
                                            <td className="td font-mono text-xs">
                                                {r.row}
                                            </td>
                                            <td className="td text-xs text-rose-600">
                                                {r.reason}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn-ghost"
                        disabled={busy}
                    >
                        {result ? '关闭' : '取消'}
                    </button>
                    <button
                        type="button"
                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={onSubmit}
                        disabled={!file || busy}
                    >
                        {busy ? '上传中…' : '上传导入'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ParamImportModal;
